import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Image } from 'primereact/image'
import { useLocation } from 'react-router-dom'
import { CommonContext } from '../../component/common/context/commonContext'
import { useMountEffect } from 'primereact/hooks'
import PdaTitle from '../../component/common/PdaTitle'
import { InventorySurveyByLocationDetail, InventorySurveyGoodsDetail } from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { getImageUrl } from '../../utils/imageUtils'
import { markDayHyphen } from '../../utils/inputText'
import moment from 'moment'
import InventorySurveyDetailDialog from '../../component/inventory/InventorySurveyDetailDialog'
import InventorySurveyShlfYmd from '../../component/inventory/InventorySurveyShlfYmd'
import { maxBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const InventorySurveyLocationDetail = () => {
    const location = useLocation()
    const { $http, $toast } = useContext(CommonContext)
    const { t } = useTranslation()
    const [locationDetail, setLocationDetail] = useState<InventorySurveyByLocationDetail>()
    const [invnGoods, setInvnGoods] = useState<InventorySurveyGoodsDetail[]>([])
    const [inventorySurveyDetailVisible, setInventorySurveyDetailVisible] = useState<boolean>(false)
    const [inventorySurveyShlfYmdVisible, setInventorySurveyShlfYmdVisible] = useState<boolean>(false)
    const [inventorySurveyDetails, setInventorySurveyDetails] = useState<InventorySurveyGoodsDetail>()
    const [maxNumber, setMaxNumber] = useState<number>(0)

    useMountEffect(() => {
        findLocationDetail()
    })

    const findLocationDetail = useCallback(async () => {
        if (location.state.invnsuId && location.state.lctSeq) {
            const invnsuId = location.state.invnsuId
            const lctSeq = location.state.lctSeq
            const {
                locationDetail,
                goodsList,
            }: {
                locationDetail: InventorySurveyByLocationDetail
                goodsList: InventorySurveyGoodsDetail[]
            } = await $http().get(`/inventory-survey/${invnsuId}`, {
                params: {
                    lctSeq: lctSeq,
                },
            })
            if (!locationDetail) {
                $toast({
                    title: t(tokens.inventory.location_detail_inquiry),
                    text: `[${t(tokens.inventory.location_code)}=${lctSeq}] ${t(tokens.common.no_inquiry_information_found)}`,
                    icon: 'success',
                })
                return
            }
            goodsList.forEach((v) => {
                v.invnsuShlfYmdDt = v.invnsuShlfYmd ? moment(v.invnsuShlfYmd).toDate() : ''
            })
            setLocationDetail(locationDetail)
            setInvnGoods(goodsList)
        }
    }, [$http, $toast, location.state.invnsuId, location.state.lctSeq])

    useEffect(() => {
        let number = 0
        if (inventorySurveyDetails && invnGoods) {
            number =
                maxBy(
                    invnGoods.filter((v) => v.invnsuId === inventorySurveyDetails.invnsuId && v.goodsId === inventorySurveyDetails.goodsId),
                    'invnSeq',
                )?.invnSeq || 0
        }
        setMaxNumber(number)
    }, [inventorySurveyDetails, invnGoods])

    const getFontColor = (lctTypCd: string) => {
        switch (lctTypCd) {
            case 'KP':
                return 'rdms-text-deepblue'
            case 'PK':
                return 'rdms-text-danger'
            default:
                return ''
        }
        // DB, ET, JB, MV, OT
    }

    const onDetailHide = (isChange: boolean) => {
        setInventorySurveyDetailVisible(false)
        if (isChange) {
            setInventorySurveyShlfYmdVisible(true)
        }
    }

    const onShlfYmdHide = (isChange: boolean) => {
        setInventorySurveyShlfYmdVisible(false)
        if (isChange) {
            findLocationDetail()
        }
    }

    const onClickGoods = (goods: InventorySurveyGoodsDetail) => {
        if (!locationDetail) return
        if (!goods) return
        setInventorySurveyDetails(goods)
        setInventorySurveyDetailVisible(true)
    }

    return (
        <>
            <PdaTitle title={t(tokens.inventory.location_detail)} historyBack goToHome />
            {locationDetail && (
                <>
                    <div className="card grid m-3 p-1">
                        <div className="col-12 flex pb-0">
                            <div className="col-8 p-0">
                                <div className="font-bold">{t(tokens.inventory.location_code)}</div>
                            </div>
                            <div className="col-4 p-0">
                                <div className={`w-full text-right font-bold text-2xl ${getFontColor(locationDetail.lctTypCd)}`}>
                                    {locationDetail.lctTypNm}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 flex pt-0 pb-2">
                            <div className="text-sm p-0">
                                {locationDetail.lctNo} (<span className="rdms-text-warning font-bold">{locationDetail.lctSeq}</span>)
                            </div>
                        </div>
                        <Divider className="my-0" />
                        <div className="col-12 flex rdms-text-deepblue font-bold text-xl mt-1">{t(tokens.common.location_)}</div>
                        <div className="col-12 flex text-lg mt-0 pt-0">{locationDetail.lctPathNm}</div>
                        <div className="col-12 flex rdms-text-deepblue font-bold text-xl mt-1">{t(tokens.inventory.location_temperature)}</div>
                        <div className="col-12 flex text-lg mt-0 pt-0">{locationDetail.goodsTmprNm}</div>
                        {/*<div className="col-12 flex rdms-text-deepblue font-bold text-xl mt-1">적치상품갯수 / 적치율</div>*/}
                        {/*<div className="col-12 flex text-lg mt-0 pt-0">*/}
                        {/*    {locationDetail.goodsIdCnt} / {locationDetail.strgRate}%*/}
                        {/*</div>*/}
                        {/*<div className="col-12 flex rdms-text-deepblue font-bold text-xl mt-1">최종변경일시</div>
                        <div className="col-12 flex text-lg mt-0 pt-0">{locationDetail.updDtFm}</div>*/}
                        {/*<div className="col-12 px-3 Location-button">*/}
                        {/*    <Button*/}
                        {/*        label="로케이션 정보 수정"*/}
                        {/*        size="large"*/}
                        {/*        className="w-full p-button-warning"*/}
                        {/*        onClick={() => onLocationChangeCheckOpen()}*/}
                        {/*    />*/}
                        {/*</div>*/}
                    </div>

                    <div className="grid m-2 mb-0 pl-2">
                        <div className="col-12 flex grid">
                            <div className="font-bold">{t(tokens.inventory.inventory_info)}</div>
                        </div>
                    </div>
                    <div className="grid m-1 mt-0 p-1 pl-3">
                        <div className="m-0 p-0 grid col-12">
                            {invnGoods &&
                                invnGoods.map((goods, index) => (
                                    <div
                                        key={index}
                                        className="card grid col-12 px-1 pb-1 pt-0 mb-3"
                                        style={{ backgroundColor: goods.invnsuDtlProgCd === 'C' ? '#a8e3bc' : '' }}
                                        onClick={() => onClickGoods(goods)}
                                    >
                                        <div className="col-4 align-self-center pt-3">
                                            <Image src={getImageUrl(goods.imageUri)} width="100%" height="130" />
                                        </div>
                                        <div className="col-8 pl-0">
                                            <div className="col-12 flex p-0 mt-2 text-left rdms-text-deepblue font-bold">
                                                {t(tokens.common.product_name)}
                                            </div>
                                            <div className="col-12 flex p-0 mt-1 pb-2">
                                                <div className="col-12 text-left text-sm p-0">{goods.goodsNm}</div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-2 text-left rdms-text-deepblue font-bold">
                                                {t(tokens.common.specification)} | {t(tokens.common.unit)} | {t(tokens.common.quantity)}
                                            </div>
                                            <div className="col-12 flex p-0 mt-1 pb-2 text-sm">
                                                {goods.specTxt} | {goods.gdslsUnitCd} |{' '}
                                                {goods.invnsuDtlProgCd === 'C' ? goods.invnsuQty : goods.invnQty}
                                            </div>
                                            <div className="col-12 flex p-0 mt-2 text-left rdms-text-deepblue font-bold">
                                                {t(tokens.common.expiration_date)}
                                            </div>
                                            <div className="col-12 flex p-0 mt-1">
                                                {/*<div className="col-5 text-left text-sm p-0">{goods.strgDay}일</div>*/}
                                                <div className="col-12 text-left text-sm p-0">
                                                    {markDayHyphen(
                                                        goods.invnsuDtlProgCd === 'C' && goods.shlfYn === 'Y' ? goods.invnsuShlfYmd : goods.shlfYmd,
                                                    )}{' '}
                                                    {goods.shlfYmd
                                                        ? `(D${moment().diff(goods.shlfYmd, 'days') > 0 ? '+' : '-'}
                                                    ${Math.abs(moment().diff(goods.shlfYmd, 'days'))})`
                                                        : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                    {inventorySurveyDetailVisible && (
                        <InventorySurveyDetailDialog
                            visible={inventorySurveyDetailVisible}
                            inventoryGoods={inventorySurveyDetails}
                            locationDetail={locationDetail}
                            onHide={(e) => onDetailHide(e)}
                        />
                    )}
                    {inventorySurveyShlfYmdVisible && (
                        <InventorySurveyShlfYmd
                            visible={inventorySurveyShlfYmdVisible}
                            inventoryGoods={inventorySurveyDetails}
                            locationDetail={locationDetail}
                            maxNumber={maxNumber}
                            onHide={(e) => onShlfYmdHide(e)}
                        />
                    )}
                </>
            )}
        </>
    )
}

export default InventorySurveyLocationDetail
